import { X } from 'lucide-react';
import { useTranslate } from '../../../locales/hooks';

type ResetConfirmDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  showLowLimitWarning?: boolean;
};

const ResetConfirmDialog: React.FC<ResetConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  showLowLimitWarning = false
}: ResetConfirmDialogProps) => {
  const t = useTranslate();

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-name="reset-confirm-dialog">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-[12px] border border-[#ddd] w-[400px] max-w-[90vw] shadow-xl">
        <div className="flex flex-col gap-[20px] p-[24px]">
          {/* Header */}
          <div className="flex items-start justify-between gap-[10px]">
            <h2 className="font-['Manrope:Bold',sans-serif] font-bold leading-[1.4] text-[#0f172a] text-[18px]">
              {t('confirmDialog__resetWarningTitle')}
            </h2>
            <button
              onClick={onClose}
              className="flex items-center justify-center p-[4px] rounded-[6px] hover:bg-[#f8fafc] transition-colors"
            >
              <X className="w-[20px] h-[20px] text-[#334155]" />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-[12px]">
            <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#334155] text-[14px]">
              {t('confirmDialog__resetWarningText')}
            </p>

            {showLowLimitWarning && (
              <div className="px-[12px] py-[10px] bg-[#fffbeb] border border-[#fde68a] rounded-[8px]">
                <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#92400e] text-[13px]">
                  {t('confirmDialog__resetWarningLowLimitText')}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-[10px]">
            <button
              onClick={onClose}
              className="px-[16px] py-[8px] rounded-[8px] border border-[#ddd] hover:bg-[#f8fafc] transition-colors"
            >
              <p className="font-['Manrope:Medium',sans-serif] font-medium text-[#334155] text-[14px]">
                {t('confirmDialog__cancelButton')}
              </p>
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="px-[16px] py-[8px] bg-[#0f172a] rounded-[8px] hover:bg-[#334155] transition-colors"
            >
              <p className="font-['Manrope:Medium',sans-serif] font-medium text-white text-[14px]">
                {t('confirmDialog__confirmButton')}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetConfirmDialog;